import moment from 'moment';

const LOCALE = 'en-US';
const CALENDAR_DAY = /^(\d{4}-\d{2}-\d{2})(?:$|[T\s])/;
const SMALL_NUMBER = 0.01;
const DATE_FORMAT = 'MMM D, YYYY';
const DATETIME_FORMAT = 'MMM D, YYYY h:mm A';
const NUMERIC_FORMATS = ['number', 'percent', 'currency', 'duration_hours'];

const numberFormat = new Intl.NumberFormat(LOCALE, {
  maximumFractionDigits: 2,
});
const smallNumberFormat = new Intl.NumberFormat(LOCALE, {
  maximumSignificantDigits: 2,
});
const compactFormat = new Intl.NumberFormat(LOCALE, {
  notation: 'compact',
  maximumSignificantDigits: 3,
});
const percentFormat = new Intl.NumberFormat(LOCALE, {
  style: 'percent',
  maximumFractionDigits: 1,
});
const currencyFormat = new Intl.NumberFormat(LOCALE, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const hoursFormat = new Intl.NumberFormat(LOCALE, {
  maximumFractionDigits: 1,
});

const asText = (value: unknown): string => {
  if (value === undefined || value === null) {
    return '';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};

const asNumber = (value: unknown): number | undefined => {
  const candidate =
    typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  return typeof candidate === 'number' && Number.isFinite(candidate)
    ? candidate
    : undefined;
};

const asCalendarDay = (value: unknown): moment.Moment | undefined => {
  const match =
    typeof value === 'string' ? CALENDAR_DAY.exec(value.trim()) : null;
  if (match === null) {
    return undefined;
  }
  const day = moment(match[1], 'YYYY-MM-DD', true);
  return day.isValid() ? day : undefined;
};

const asInstant = (value: unknown): moment.Moment | undefined => {
  if (typeof value !== 'string' || value.trim() === '') {
    return undefined;
  }
  const instant = moment.utc(value.trim(), moment.ISO_8601).local();
  return instant.isValid() ? instant : undefined;
};

const plainNumber = (number: number): string =>
  number !== 0 && Math.abs(number) < SMALL_NUMBER
    ? smallNumberFormat.format(number)
    : numberFormat.format(number);

const withNumber = (
  value: unknown,
  render: (number: number) => string,
): string => {
  const number = asNumber(value);
  return number === undefined ? asText(value) : render(number);
};

const withMoment = (
  parsed: moment.Moment | undefined,
  value: unknown,
  pattern: string,
): string => (parsed === undefined ? asText(value) : parsed.format(pattern));

export const formatCompactNumber = (value: unknown): string =>
  withNumber(value, number => compactFormat.format(number));

export const isNumericFormat = (format?: string): boolean =>
  format !== undefined && NUMERIC_FORMATS.includes(format);

export const formatValue = (value: unknown, format?: string): string => {
  switch (format) {
    case 'number':
      return withNumber(value, plainNumber);
    case 'percent':
      return withNumber(value, number => percentFormat.format(number));
    case 'currency':
      return withNumber(value, number => currencyFormat.format(number));
    case 'duration_hours':
      return withNumber(value, number => `${hoursFormat.format(number)} h`);
    case 'date':
      return withMoment(asCalendarDay(value), value, DATE_FORMAT);
    case 'datetime':
      return withMoment(asInstant(value), value, DATETIME_FORMAT);
    default:
      return asText(value);
  }
};
